import { BrDivider } from '@govbr-ds/react-components';
import AnmSwitch from 'components/anmSwitch/anmSwitch';
import { IDeducaoAdicionada, IDeducaoRecebida, IDeducoes } from 'interfaces/IDeducao';
import React, { useEffect } from 'react';
import { formatCurrency } from 'utils/formatCurrency';
import Deducao from './components/deducao';
import styles from './secaoDeducoes.module.scss';

interface ISecaoDeducoesProps {
  deducoesPossiveis: IDeducoes | undefined;
  deducoesAdicionadas: IDeducaoAdicionada[];
  setDeducoesAdicionadas: React.Dispatch<React.SetStateAction<IDeducaoAdicionada[]>>;
  semMargem?: boolean;
  haCompraTerceiro: boolean;
  setHaCompraTerceiro: React.Dispatch<React.SetStateAction<boolean>>;
}

const SecaoDeducoes = ({
  deducoesPossiveis,
  deducoesAdicionadas,
  semMargem = false,
  setDeducoesAdicionadas,
  haCompraTerceiro,
  setHaCompraTerceiro,
}: ISecaoDeducoesProps) => {
  const COMPRAS_DE_TERCEIRO = 'COMPRAS DE TERCEIRO';
  const containerClass = semMargem ? styles.containerSemMargem : styles.container;

  useEffect(() => {
    if (!haCompraTerceiro) {
      setDeducoesAdicionadas((prev) => prev.filter((deducao) => deducao.nome !== COMPRAS_DE_TERCEIRO));
    }
  }, [haCompraTerceiro]);

  const atualizarValorDeducoes = (tipoDeducao: string, novoValor: number | '') => {
    if (novoValor === '') {
      setDeducoesAdicionadas((prev) => prev.filter((da) => da.nome !== tipoDeducao));
      return;
    }

    setDeducoesAdicionadas((prev) => {
      const indiceExistente = prev.findIndex((deducao) => deducao.nome === tipoDeducao);

      if (indiceExistente >= 0) {
        return prev.map((deducao, index) => (index === indiceExistente ? { ...deducao, valor: novoValor } : deducao));
      }

      return [...prev, { nome: tipoDeducao, valor: novoValor }];
    });
  };

  const total = Number(deducoesAdicionadas.reduce((acumulador, deducao) => acumulador + deducao.valor, 0).toFixed(2));

  const compraTerceirosHtml = (comprasDeTerceiros: IDeducaoRecebida[] | undefined | []) => {
    if (comprasDeTerceiros === undefined || comprasDeTerceiros.length === 0) return;
    return (
      <div className={styles.containerCompraTerceiros}>
        <div className={styles.camposContainer}>
          <div className={styles.switchContainer}>
            <AnmSwitch
              switchDataTestId='switch-compra-terceiro'
              label='Deseja abater compras de terceiros?'
              value={haCompraTerceiro}
              setValue={setHaCompraTerceiro}
              tooltipText='Essa marcação só deve ser feita quando o bem mineral adquirido de terceiros for blendado com minério de produção própria e comercializado no CFOP de produção do estabelecimento.'
            />
          </div>
          <div className={styles.selectContainer}>
            {haCompraTerceiro && (
              <Deducao
                key={COMPRAS_DE_TERCEIRO}
                nome={COMPRAS_DE_TERCEIRO}
                isViewOnly={false}
                onChange={atualizarValorDeducoes}
                hideLabel={true}
                deducaoAdicionada={deducoesAdicionadas.find((da) => da.nome === COMPRAS_DE_TERCEIRO)}
              ></Deducao>
            )}
          </div>
        </div>
        <BrDivider className={styles.divider}></BrDivider>
      </div>
    );
  };

  const deducoesHtml = (
    deducoesRecebidas: IDeducaoRecebida[] | undefined | [],
    deducoesAdicionadas: IDeducaoAdicionada[],
    tituloSecao: string,
    incluiDivider?: boolean
  ) => {
    if (!deducoesRecebidas || deducoesRecebidas.length === 0) return null;

    const deducoesRegulares = deducoesRecebidas.map((deducao) => (
      <Deducao
        key={deducao.id}
        nome={deducao.nome}
        isViewOnly={false}
        onChange={atualizarValorDeducoes}
        deducaoAdicionada={deducoesAdicionadas.find((da) => da.nome === deducao.nome)}
      ></Deducao>
    ));

    return (
      <>
        <div className={styles.containerInterno}>
          <div className={styles.containerTituloInterno}>
            <span className={styles.titulo}>{tituloSecao}</span>
          </div>
          <BrDivider className={styles.divider}></BrDivider>
          {deducoesRegulares}
        </div>
        {incluiDivider && <BrDivider className={styles.divider}></BrDivider>}
      </>
    );
  };

  const apresentarDeducao = (deducoesPossiveis: IDeducoes | undefined) => {
    if (
      deducoesPossiveis &&
      (deducoesPossiveis.comprasDeTerceiro.length > 0 ||
        deducoesPossiveis.tributosEspeciais.length > 0 ||
        deducoesPossiveis.tributosRegulares.length > 0)
    ) {
      return (
        <div className={containerClass}>
          <span className={styles.titulo}>Deduções</span>
          <BrDivider className={styles.divider}></BrDivider>
          <label>Total Deduções (R$)</label>
          <span>{formatCurrency(total)}</span>
          <BrDivider className={styles.divider}></BrDivider>
          {compraTerceirosHtml(deducoesPossiveis?.comprasDeTerceiro)}
          {deducoesHtml(deducoesPossiveis?.tributosRegulares, deducoesAdicionadas, 'Tributos Incidentes')}
          {deducoesHtml(deducoesPossiveis?.tributosEspeciais, deducoesAdicionadas, 'Outras Deduções')}
        </div>
      );
    }
    return <></>;
  };

  return apresentarDeducao(deducoesPossiveis);
};

export default SecaoDeducoes;
